import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useLocation } from 'react-router-dom';

export interface TourStep {
  id: number;
  page: string;
  elementId: string | null;
  title: string;
  description: string;
  icon: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 0,
    page: 'modal',
    elementId: null,
    title: 'Bem-vindo ao MarketPro Tools! 🚀',
    description: 'Vamos fazer um tour rápido pela plataforma para você conhecer todos os recursos disponíveis.',
    icon: 'Sparkles',
    position: 'center'
  },
  {
    id: 1,
    page: '/dashboard',
    elementId: 'tour-header',
    title: 'Seu Painel de Controle',
    description: 'Este é o Dashboard, onde você acompanha suas estatísticas, créditos e progresso na plataforma.',
    icon: 'LayoutDashboard',
    position: 'bottom'
  },
  {
    id: 2,
    page: '/dashboard',
    elementId: 'tour-analytics',
    title: 'Estatísticas e Analytics',
    description: 'Veja suas métricas de uso, histórico de conversas com IA e acompanhe seu consumo de créditos.',
    icon: 'BarChart3',
    position: 'bottom'
  },
  {
    id: 3,
    page: '/dashboard',
    elementId: 'tour-smartcheck-card',
    title: 'SmartCheck - Checklists Inteligentes',
    description: 'Crie e acompanhe checklists personalizados para suas metas de marketing. A IA ajuda você a definir os passos!',
    icon: 'CheckSquare',
    position: 'left'
  },
  {
    id: 4,
    page: '/ferramentas',
    elementId: 'tour-sidebar',
    title: 'Categorias de Ferramentas',
    description: 'Navegue pelas diferentes categorias: Agentes de IA, Prompts, Planilhas, E-books e muito mais.',
    icon: 'FolderOpen',
    position: 'right'
  },
  {
    id: 5,
    page: '/ferramentas',
    elementId: 'tour-agents',
    title: 'Agentes GPT Especializados',
    description: 'Converse com especialistas de IA treinados para marketing, copywriting, estratégia e mais.',
    icon: 'Bot',
    position: 'bottom'
  },
  {
    id: 6,
    page: '/ferramentas',
    elementId: 'tour-spreadsheets',
    title: 'Planilhas Inteligentes',
    description: 'Planilhas prontas para gestão financeira, funis de venda, tráfego pago e análise de métricas.',
    icon: 'Table',
    position: 'bottom'
  },
  {
    id: 7,
    page: '/ferramentas',
    elementId: 'tour-product-dna',
    title: 'DNA de Produto',
    description: 'Configure informações do seu negócio uma vez e a IA usará em todas as conversas automaticamente.',
    icon: 'Dna',
    position: 'left'
  },
  {
    id: 8,
    page: '/smartcheck',
    elementId: null,
    title: 'SmartCheck - Execução Guiada',
    description: 'Transforme objetivos em ações! Crie checklists com IA ou use templates prontos e ganhe pontos.',
    icon: 'Target',
    position: 'center'
  },
  {
    id: 9,
    page: 'modal',
    elementId: null,
    title: 'Pronto para Começar! 🎉',
    description: 'Você conheceu os principais recursos. Explore a plataforma e conte com a IA para acelerar seus resultados!',
    icon: 'PartyPopper',
    position: 'center'
  }
];

export interface WelcomeTourState {
  isActive: boolean;
  currentStep: number;
  isCompleted: boolean;
  isLoading: boolean;
}

export function useWelcomeTour() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [state, setState] = useState<WelcomeTourState>({
    isActive: false,
    currentStep: 0,
    isCompleted: true,
    isLoading: true
  });

  // Load tour state from profile
  useEffect(() => {
    if (profile) {
      const completed = (profile as any).welcome_tour_completed ?? false;
      const step = (profile as any).welcome_tour_step ?? 0;
      
      setState(prev => ({
        ...prev,
        isCompleted: completed,
        currentStep: step,
        isLoading: false
      }));
    }
  }, [profile]);

  // Start the tour
  const startTour = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: true,
      currentStep: 0
    }));
  }, []);

  // Go to next step
  const nextStep = useCallback(async () => {
    const nextStepIndex = state.currentStep + 1;
    
    if (nextStepIndex >= TOUR_STEPS.length) {
      await completeTour();
      return;
    }

    const nextStepData = TOUR_STEPS[nextStepIndex];
    
    // Navigate if needed
    if (nextStepData.page !== 'modal' && location.pathname !== nextStepData.page) {
      navigate(nextStepData.page);
    }

    setState(prev => ({
      ...prev,
      currentStep: nextStepIndex
    }));

    // Save progress to database
    if (user) {
      await supabase
        .from('profiles')
        .update({ welcome_tour_step: nextStepIndex })
        .eq('user_id', user.id);
    }
  }, [state.currentStep, location.pathname, navigate, user]);

  // Go to previous step
  const prevStep = useCallback(async () => {
    if (state.currentStep <= 0) return;

    const prevStepIndex = state.currentStep - 1;
    const prevStepData = TOUR_STEPS[prevStepIndex];

    // Navigate if needed
    if (prevStepData.page !== 'modal' && location.pathname !== prevStepData.page) {
      navigate(prevStepData.page);
    }

    setState(prev => ({
      ...prev,
      currentStep: prevStepIndex
    }));

    // Save progress
    if (user) {
      await supabase
        .from('profiles')
        .update({ welcome_tour_step: prevStepIndex })
        .eq('user_id', user.id);
    }
  }, [state.currentStep, location.pathname, navigate, user]);

  // Skip/Complete the tour
  const completeTour = useCallback(async () => {
    setState(prev => ({
      ...prev,
      isActive: false,
      isCompleted: true,
      currentStep: TOUR_STEPS.length - 1
    }));

    if (user) {
      await supabase
        .from('profiles')
        .update({
          welcome_tour_completed: true,
          welcome_tour_completed_at: new Date().toISOString(),
          welcome_tour_step: TOUR_STEPS.length - 1
        })
        .eq('user_id', user.id);
    }
  }, [user]);

  // Skip tour without completing
  const skipTour = useCallback(async () => {
    await completeTour();
  }, [completeTour]);

  // Reset tour (for "Rever Tour" feature)
  const resetTour = useCallback(async () => {
    setState(prev => ({
      ...prev,
      isActive: true,
      currentStep: 0,
      isCompleted: false
    }));

    if (user) {
      await supabase
        .from('profiles')
        .update({
          welcome_tour_completed: false,
          welcome_tour_completed_at: null,
          welcome_tour_step: 0
        })
        .eq('user_id', user.id);
    }
  }, [user]);

  // Get current step data
  const currentStepData = TOUR_STEPS[state.currentStep] || TOUR_STEPS[0];

  // Check if should auto-start tour
  const shouldStartTour = !state.isLoading && !state.isCompleted && !state.isActive && !!user;

  return {
    ...state,
    currentStepData,
    totalSteps: TOUR_STEPS.length,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    completeTour,
    resetTour,
    shouldStartTour
  };
}
